import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import { setMarket } from "../../../redux/slices/uiSlice";
import {
  buildStickerFilename,
  editStickerPdf,
  isPdfFile,
} from "../../../utils/stickerPdfEditor";

const MAX_FILE_SIZE = 20 * 1024 * 1024;

function getTodayValue() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatFileSize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

export default function StickerEditorPage() {
  const market = useSelector((state) => state.ui.market);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const inputVersionRef = useRef(0);

  const [file, setFile] = useState(null);
  const [expiryDate, setExpiryDate] = useState(getTodayValue);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [dragActive, setDragActive] = useState(false);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  function clearResult() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setResult(null);
  }

  function selectFile(nextFile) {
    if (processing) return;
    clearResult();
    if (!nextFile) {
      setFile(null);
      return;
    }
    if (!isPdfFile(nextFile)) {
      enqueueSnackbar("Выберите файл в формате PDF", { variant: "error" });
      return;
    }
    if (nextFile.size > MAX_FILE_SIZE) {
      enqueueSnackbar("Размер PDF не должен превышать 20 МБ", {
        variant: "error",
      });
      return;
    }
    inputVersionRef.current += 1;
    setFile(nextFile);
  }

  async function handleProcess() {
    if (!file || !expiryDate) return;
    const processingVersion = inputVersionRef.current;
    setProcessing(true);
    clearResult();
    try {
      const nextResult = await editStickerPdf(file, expiryDate);
      if (processingVersion !== inputVersionRef.current) return;
      const nextPreviewUrl = URL.createObjectURL(nextResult.blob);
      setResult(nextResult);
      setPreviewUrl(nextPreviewUrl);
      enqueueSnackbar("PDF успешно обработан", { variant: "success" });
    } catch (error) {
      console.error("Не удалось обработать PDF", error);
      enqueueSnackbar(error.message || "Не удалось обработать PDF", {
        variant: "error",
      });
    } finally {
      setProcessing(false);
    }
  }

  function handleDownload() {
    if (!result) return;
    const url = URL.createObjectURL(result.blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = buildStickerFilename(file?.name, expiryDate);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <DashboardLayout
      market={market}
      onSelectMarket={(value) => dispatch(setMarket(value))}
    >
      <Stack spacing={2} sx={{ maxWidth: 1100 }}>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Редактор стикеров
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Загрузите PDF со стикерами, выберите срок годности и скачайте
            готовый файл без логотипа CloudMark. Обработка выполняется в
            браузере — файл не загружается на сервер.
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={3}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => {
                if (!processing) fileInputRef.current?.click();
              }}
              onKeyDown={(event) => {
                if (
                  !processing &&
                  (event.key === "Enter" || event.key === " ")
                ) {
                  event.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragActive(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setDragActive(false);
                if (!processing) selectFile(event.dataTransfer.files?.[0]);
              }}
              sx={{
                border: "2px dashed",
                borderColor: dragActive
                  ? "primary.main"
                  : file
                    ? "success.main"
                    : "divider",
                bgcolor: dragActive ? "action.hover" : "background.default",
                borderRadius: 2,
                px: 3,
                py: 4,
                textAlign: "center",
                cursor: processing ? "not-allowed" : "pointer",
                opacity: processing ? 0.7 : 1,
                transition:
                  "border-color 150ms ease, background-color 150ms ease",
                "&:focus-visible": {
                  outline: "3px solid",
                  outlineColor: "primary.light",
                },
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                hidden
                disabled={processing}
                onChange={(event) => selectFile(event.target.files?.[0])}
              />
              {file ? (
                <Stack spacing={1} alignItems="center">
                  <PictureAsPdfRoundedIcon
                    color="error"
                    sx={{ fontSize: 42 }}
                  />
                  <Typography
                    sx={{ fontWeight: 600, wordBreak: "break-word" }}
                  >
                    {file.name}
                  </Typography>
                  <Chip size="small" label={formatFileSize(file.size)} />
                  <Typography variant="caption" color="text.secondary">
                    Нажмите, чтобы выбрать другой PDF
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={1} alignItems="center">
                  <UploadFileRoundedIcon
                    color="primary"
                    sx={{ fontSize: 42 }}
                  />
                  <Typography sx={{ fontWeight: 600 }}>
                    Перетащите PDF сюда или нажмите для выбора
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Стикеры 58 × 40 мм, до 20 МБ
                  </Typography>
                </Stack>
              )}
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "flex-end" }}
            >
              <TextField
                type="date"
                label="Срок годности"
                value={expiryDate}
                onChange={(event) => {
                  inputVersionRef.current += 1;
                  setExpiryDate(event.target.value);
                  clearResult();
                }}
                disabled={processing}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: 230 }}
              />
              <Button
                variant="contained"
                size="large"
                onClick={handleProcess}
                disabled={!file || !expiryDate || processing}
                startIcon={
                  processing ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <CheckCircleOutlineRoundedIcon />
                  )
                }
              >
                {processing ? "Обработка…" : "Обработать PDF"}
              </Button>
            </Stack>

            <Alert severity="info">
              Логотип удаляется и выбранная дата добавляется на каждую страницу
              PDF. QR-код и штрихкод остаются без изменений.
            </Alert>
          </Stack>
        </Paper>

        {result && previewUrl && (
          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={2}
              sx={{
                p: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>Готовый PDF</Typography>
                <Typography variant="body2" color="text.secondary">
                  Страниц: {result.pageCount} · Срок годности:{" "}
                  {result.formattedDate}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<DownloadRoundedIcon />}
                onClick={handleDownload}
              >
                Скачать PDF
              </Button>
            </Stack>
            <Box
              component="iframe"
              title="Предпросмотр обработанного PDF"
              src={previewUrl}
              sx={{
                display: "block",
                width: "100%",
                height: { xs: 420, md: 620 },
                border: 0,
                bgcolor: "grey.100",
              }}
            />
          </Paper>
        )}
      </Stack>
    </DashboardLayout>
  );
}
